import { useEffect, useState } from 'react';
import { KalturaPlayerProvider } from 'kaltura-player';
import { updateLanguage } from '../../translations/i18n';
import { GlobalSettingsProvider } from '../../hooks/useGlobalSettings';
import { checkFetchError } from '../../utils/checkFetchError';
import SESSION_STUB from '../../stubs/GetVideoFromCode';
import { PlayerPage } from './PlayerPage';
import { Analytics, applyUserCookies } from '../../utils/Analytics';
import * as Sentry from '@sentry/react';

import {
	getCodeFromURL,
	storeVideoState,
	redirectIfLegacyUrl,
	ignoreMissingLinkInDev,
	filterMSBots,
} from '../../utils/utils';
import { gv_analytics } from 'utils/Analytics';

const from_shared_link = window.location.href.match(
	/from_shared_link|\/share\//
)
	? 1
	: 0;

const bundlerUrl = process.env.REACT_APP_KALTURA_URL;
const partnerId = process.env.REACT_APP_KALTURA_PARTNER_ID;
const uiConfId = process.env.REACT_APP_KALTURA_UI_CONF_ID;

const getSessionData = async (code, { from_shared_link }) => {
	return !code && process.env.NODE_ENV !== 'production'
		? { GV_CODE: code, ...SESSION_STUB() }
		: await fetch(
				`/api/getVideoFromCode?${new URLSearchParams({
					code,
					from_shared_link,
				})}`
		  )
				.then(checkFetchError)
				.then((session) => {
					return { GV_CODE: code, ...session };
				});
};

const Player = ({ embed = false }) => {
	try {
		sessionStorage.setItem('app_name', 'PLAYER');
	} catch (e) {
		Sentry.captureMessage('Could not store app_name in sessionStorage');
	}

	const [session, setSession] = useState(null);

	useEffect(() => {
		getCodeFromURL(window.location.href) // Gets standard code from URL
			.then(filterMSBots)
			.catch(redirectIfLegacyUrl) // Check for legacy URL if not found
			.catch(ignoreMissingLinkInDev)
			.then((GV_CODE) =>
				getSessionData(GV_CODE, {
					from_shared_link,
				})
			) // Use code to query server for session data
			.then((session) => {
				/**
				 * Cast disable_analytics flag to boolean
				 * Needs to handle both string and int versions of 0 and 1
				 */
				// console.log('SESH', session.disable_analytics);
				session.disable_analytics = !!parseInt(
					session.disable_analytics
				);
				console.log(
					'Analytics',
					session.disable_analytics ? 'disabled' : 'enabled'
				);
				// Add code to response object for later use
				return session;
			})
			.then((session) => {
				applyUserCookies(session.userData?.cookies?.split(','));

				// TODO refactor this .setContext to be declarative, as part of <Tracker> component below?
				gv_analytics.addContext({ 'gv-metadata': session });
				const {
					// GV_CODE,
					video: { videoID, language, name },
				} = session;

				// Update localstorage with date of most recent load
				storeVideoState(videoID, 'loaded');

				updateLanguage(language);

				document.title = `${process.env.REACT_APP_BRAND_NAME} | ${name}`;
				setSession(session);
			})
			.catch((e) => {
				// Hack to trigger the ErrorBoundary at the top level from within a hook
				// See: https://github.com/facebook/react/issues/14981
				setSession(() => {
					throw e;
				});
			});
	}, []);

	return session ? (
		<GlobalSettingsProvider
			value={{
				from_shared_link,
				contactEmail:
					session.featuresConfig.custom_feedback_email ||
					process.env.REACT_APP_FEEDBACK_EMAIL,
				...session,
			}}
		>
			<KalturaPlayerProvider
				playerBundleConfig={{
					ks: session.userData.CDN_SESSION_ID,
					bundlerUrl,
					partnerId,
					uiConfId,
				}}
			>
				<Analytics
					app_name={'PLAYER'}
					brand_name={process.env.REACT_APP_BRAND_NAME}
					disable_analytics={session?.disable_analytics}
					from_shared_link={
						session?.is_shared_link === 1 || from_shared_link === 1
					} // TODO This is a total hack - let's decide on front or back and stick with it
					userProps={{
						userID: session?.userData.userID,
						companyID: session?.userData.companyID,
						companyName: session?.userData.companyName,
						companyInternalGroup:
							session?.userData.companyInternalGroup,
						companyInternalUID:
							session?.userData.companyInternalUID,
					}}
					sessionProps={{
						GVID: session?.video.videoID.slice(0, 8),
						videoID: session?.video.videoID,
						videoName: session?.video.name,
						language: session?.video.language,
					}}
				/>
				<PlayerPage embed={embed} {...session.video} />
			</KalturaPlayerProvider>
		</GlobalSettingsProvider>
	) : null;
};

export default Player; // default export is required for react lazy loading
